/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import {
  AnnotationSettings,
  AnnotationSettingsInfo,
  AnnotationSettingsTabType,
  CurrentSelectionResult,
} from '../../../models/annotation-settings';
import {
  IAnnotationSettingsFacade,
  ANNOTATION_SETTINGS_FACADE_TOKEN,
} from '../../../../../../data-access/src/lib/domain/facades-models/annotation-settings-facade-interface';
import { GlobalConstants } from '../../../constants/global-constants';
import { Subject, switchMap, take, takeUntil } from 'rxjs';
import { IAnnotationSettingsInterface } from '../../../../../../data-access/src/lib/domain/facades-models/annotation-settings-interface';
import { AnnotationSettingsReturnCodes } from '../../../models/annotation-settings-codes';
import { AlertStatus, IAlert } from "../../../../../../../shared/ui/src/lib/components/alert/alert-types";

@Component({
  selector: 'sysque-annotation-settings',
  templateUrl: './annotation-settings.component.html',
  styleUrls: ['./annotation-settings.component.scss'],
  standalone: false
})
export class AnnotationSettingsComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter();
  @ViewChildren('child') childComponents!: QueryList<IAnnotationSettingsInterface>;
  public data: AnnotationSettingsInfo = {
    textId: '',
    discardButtonName: 'Discard',
    saveButtonName: 'Save',
    applyToSelectionButtonName: 'Apply to Selection',
  };
  public dataLoadedSubject = new Subject<void>();
  public dataLoaded$ = this.dataLoadedSubject.asObservable(); 
  public componentTitle = 'Annotation Settings';
  public alertStatus: AlertStatus = AlertStatus.Warning;
  public warningMessage = '';
  public isDiscardButtonDisabled = false;
  public isApplyButtonDisabled = false;
  public currentSelectionResult: CurrentSelectionResult = new CurrentSelectionResult();
  public buttonName = '';
  private readonly closed$ = new Subject<void>();
  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: true,
    };
  }

  constructor(@Inject(ANNOTATION_SETTINGS_FACADE_TOKEN) private annotationSettingsFacade: IAnnotationSettingsFacade) {
    this.annotationSettingsFacade.getRevitInformation();
    this.loadData();
  }

  public ngOnInit(): void {
    window.addEventListener('storage', this.handleStorageEvent.bind(this));
  }

  public ngOnDestroy(): void {
    window.removeEventListener('storage', this.handleStorageEvent.bind(this));
    this.closed$.next();
    this.closed$.complete();
  }

  public loadData(): void {
    this.annotationSettingsFacade
      .getAnnotationSettings()
      .pipe(
        switchMap((data) => {
          this.annotationSettingsFacade.updateStorage(GlobalConstants.ANNOTATION_SETTINGS_DATA_FROM_XML_NAME, data);
           let revitVersion = '';
              this.annotationSettingsFacade.getRevitInformationFromStore().subscribe((data) => {
                revitVersion = data.revitVersion;
              });
              this.annotationSettingsFacade.updateStorage(GlobalConstants.REVIT_VERSION, revitVersion);
          return this.annotationSettingsFacade.isCurrentSelectionValid();
        }),
        takeUntil(this.closed$)
      )
      .subscribe((data) => {
        this.currentSelectionResult = data;
        this.buttonName = this.currentSelectionResult.validForCableTray
          ? this.data.applyToSelectionButtonName
          : this.data.saveButtonName;
        this.removeSelectionsFromStorage();
        // Emit event after data is fully loaded
        this.dataLoadedSubject.next();  
      });
    }

  public handleValidationResult(currentSelectionResult: CurrentSelectionResult): void {
    let selectedTab = this.childComponents.first.selectedTab;
    if (selectedTab === AnnotationSettingsTabType.CableTray) {
      this.buttonName = currentSelectionResult.validForCableTray
        ? this.data.applyToSelectionButtonName
        : this.data.saveButtonName;
    } else if (selectedTab === AnnotationSettingsTabType.Conduit) {
      this.buttonName = currentSelectionResult.validForConduit
        ? this.data.applyToSelectionButtonName
        : this.data.saveButtonName;
    }
  }

  public apply(): void {
    let isOnlySaved = false;
    let selectedTab = AnnotationSettingsTabType.CableTray;
    selectedTab = this.childComponents.first.selectedTab;
    this.annotationSettingsFacade.saveAnnotationSettings(this.collectDataToSaveInXmlFile());
    isOnlySaved = true;

    //apply to selection if the current selection is valid based on the selected tab
    if (
      (selectedTab == AnnotationSettingsTabType.CableTray && this.currentSelectionResult.validForCableTray) || 
      selectedTab == AnnotationSettingsTabType.Conduit && this.currentSelectionResult.validForConduit
    ) {
      //apply to selection
      this.annotationSettingsFacade.applyData(selectedTab);
      isOnlySaved = false;
    }
    if (isOnlySaved) {
      this.showAlert(GlobalConstants.SAVE_DATA_SUCCESS, AlertStatus.Success);
    }
  }

  public handleStorageEvent(event: StorageEvent): void {
    if (event.key === GlobalConstants.ApplyDataResponse) {
      if (event.newValue === '') {
        return;
      }
      const response = JSON.parse(event.newValue);
      if (response == AnnotationSettingsReturnCodes.Success) {
        this.showAlert(GlobalConstants.APPLY_DATA_SUCCESS, AlertStatus.Success);
      } else if (response == AnnotationSettingsReturnCodes.NoSelection) {
        this.showAlert(GlobalConstants.PLEASE_SELECT_ONE_ELEMENT, AlertStatus.Warning);
      } else if (response == AnnotationSettingsReturnCodes.UnlockedView) {
        this.showAlert(GlobalConstants.VIEW_3D_UNLOCKED, AlertStatus.Info);
      } else if (response == AnnotationSettingsReturnCodes.IncorrectFamilyTag) {
        this.showAlert(GlobalConstants.SELECTED_TAG_WRONG_CATEGORY, AlertStatus.Warning);
      }
    }
  }

  // get data from xml file and resave in the local storage!!!
  // reload the first child which is reloading the all children hierarchically
  public discard(): void {
    this.refreshLocalStorageWithXmlData();
    this.childComponents.forEach((child) => {
      child.reload();
    });
    this.hideAlert();
  }

  /**
   * Dismiss the alert
   */
  public onDismissClick(): void {
    this.hideAlert();
  }

  //#region private methods

  private showAlert(message: string, alertStatus: AlertStatus): void {
    this.warningMessage = message;
    this.alertStatus = alertStatus;
  }

  private hideAlert() {
    this.warningMessage = '';
  }

  //remove all the selections from storage
  private removeSelectionsFromStorage(): void {
    this.annotationSettingsFacade.updateStorage(GlobalConstants.CABLE_TRAY_ELEVATION_DATA_SELECTION_NAME, null);
    this.annotationSettingsFacade.updateStorage(GlobalConstants.CABLE_TRAY_ITEM_NUMBER_DATA_SELECTION_NAME, null);
    this.annotationSettingsFacade.updateStorage(GlobalConstants.CABLE_TRAY_SIZE_DATA_SELECTION_NAME, null);
    this.annotationSettingsFacade.updateStorage(GlobalConstants.CABLE_TRAY_SPOOL_DATA_SELECTION_NAME, null);

    this.annotationSettingsFacade.updateStorage(GlobalConstants.CONDUIT_SETTINGS_ELEVATION_DATA_SELECTION_NAME, null);
    this.annotationSettingsFacade.updateStorage(GlobalConstants.CONDUIT_SETTINGS_ITEM_NUMBER_DATA_SELECTION_NAME, null);
    this.annotationSettingsFacade.updateStorage(GlobalConstants.CONDUIT_SETTINGS_SIZE_DATA_SELECTION_NAME, null);
    this.annotationSettingsFacade.updateStorage(GlobalConstants.CONDUIT_SETTINGS_SPOOL_DATA_SELECTION_NAME, null);
  }

  // collect data from local storage in order to save data in xml file
  private collectDataToSaveInXmlFile(): AnnotationSettings {
    const data: AnnotationSettings = new AnnotationSettings();
    const ctSelection = [
      this.annotationSettingsFacade.getTagDataFromLocalStorageByTagName(
        GlobalConstants.CABLE_TRAY_SIZE_DATA_SELECTION_NAME
      ),
      this.annotationSettingsFacade.getTagDataFromLocalStorageByTagName(
        GlobalConstants.CABLE_TRAY_ELEVATION_DATA_SELECTION_NAME
      ),
      this.annotationSettingsFacade.getTagDataFromLocalStorageByTagName(
        GlobalConstants.CABLE_TRAY_ITEM_NUMBER_DATA_SELECTION_NAME
      ),
      this.annotationSettingsFacade.getTagDataFromLocalStorageByTagName(
        GlobalConstants.CABLE_TRAY_SPOOL_DATA_SELECTION_NAME
      ),
    ].filter((x) => x !== null);

    ctSelection.forEach((x) => data.cableTrayAnnotation.push(x));

    const csSelection = [
      this.annotationSettingsFacade.getTagDataFromLocalStorageByTagName(
        GlobalConstants.CONDUIT_SETTINGS_SIZE_DATA_SELECTION_NAME
      ),
      this.annotationSettingsFacade.getTagDataFromLocalStorageByTagName(
        GlobalConstants.CONDUIT_SETTINGS_ELEVATION_DATA_SELECTION_NAME
      ),
      this.annotationSettingsFacade.getTagDataFromLocalStorageByTagName(
        GlobalConstants.CONDUIT_SETTINGS_ITEM_NUMBER_DATA_SELECTION_NAME
      ),
      this.annotationSettingsFacade.getTagDataFromLocalStorageByTagName(
        GlobalConstants.CONDUIT_SETTINGS_SPOOL_DATA_SELECTION_NAME
      ),
    ].filter((x) => x !== null);
    csSelection.forEach((x) => data.conduitAnnotation.push(x));

    return data;
  }

  // get data from xml file and resave in local storage
  private refreshLocalStorageWithXmlData(): void {
    this.annotationSettingsFacade
      .getAnnotationSettings()
      .pipe(take(1))
      .subscribe((data) => {
        this.annotationSettingsFacade.updateStorage(GlobalConstants.ANNOTATION_SETTINGS_DATA_FROM_XML_NAME, data);
      });
  }

  //#endregion
}
